import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  Paper,
  Typography,
  Chip
} from '@mui/material';
import TableRowsIcon from '@mui/icons-material/TableRows';

interface CSVPreviewTableProps {
  rawCsvRows: string[][];
  headerRowOffset: number;
  onRowClick: (rowIndex: number) => void;
  maxColumnsToShow?: number;
}

const isEmptyRow = (row: string[]) =>
  !row || !row.some((c) => c != null && String(c).trim().length > 0);

const CSVPreviewTable: React.FC<CSVPreviewTableProps> = ({
  rawCsvRows,
  headerRowOffset,
  onRowClick,
  maxColumnsToShow = 10
}) => {
  const previewRows = rawCsvRows.slice(0, 10);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          mb: 1.5
        }}
      >
        {headerRowOffset >= 0 && (
          <Chip
            size="small"
            label={`Header row: ${headerRowOffset}`}
            color="primary"
            variant="outlined"
          />
        )}
        <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
          Select the row that contains your column headers
        </Typography>
      </Box>
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: '300px',
          overflow: 'auto',
          border: '1px solid',
          borderColor: 'divider',
          mb: 2
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600, width: 80 }}>Row</TableCell>
              <TableCell sx={{ fontWeight: 600 }} colSpan={maxColumnsToShow}>
                Preview
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {previewRows.map((row, rowIndex) => {
              const empty = isEmptyRow(row);
              const selected = rowIndex === headerRowOffset;
              return (
                <TableRow
                  key={rowIndex}
                  sx={{
                    backgroundColor: selected
                      ? (theme) => theme.palette.action.selected
                      : 'transparent',
                    cursor: 'pointer',
                    opacity: empty ? 0.6 : 1,
                    '&:hover': {
                      backgroundColor: (theme) => theme.palette.action.hover
                    }
                  }}
                  onClick={() => onRowClick(rowIndex)}
                >
                  <TableCell
                    sx={{
                      fontWeight: selected ? 700 : 400,
                      position: 'sticky',
                      left: 0,
                      zIndex: 1,
                      backgroundColor: selected
                        ? (theme) => theme.palette.action.selected
                        : (theme) => theme.palette.background.paper
                    }}
                  >
                    {rowIndex}
                    {selected && (
                      <TableRowsIcon
                        sx={{
                          fontSize: 14,
                          ml: 0.5,
                          verticalAlign: 'middle'
                        }}
                      />
                    )}
                  </TableCell>
                  {row.slice(0, maxColumnsToShow).map((cell, colIndex) => (
                    <TableCell key={colIndex}>
                      {empty
                        ? '(empty)'
                        : `${String(cell || '').substring(0, 50)}${String(cell || '').length > 50 ? '...' : ''}`}
                    </TableCell>
                  ))}
                  {row.length > maxColumnsToShow && <TableCell>...</TableCell>}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {rawCsvRows.length > 10 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 1 }}
        >
          Showing first 10 of {rawCsvRows.length} rows
        </Typography>
      )}
    </>
  );
};

export default CSVPreviewTable;
